import { Button } from '@/components/ui/atoms/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/atoms/form'
import { Input } from '@/components/ui/atoms/input'
import { toast } from '@/components/ui/molecules/use-toast'
import { logger, open } from '@/lib/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useStore } from '@nanostores/react'
import { AtSign, Phone, User } from 'lucide-react'
import { atom } from 'nanostores'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

const required_error = 'Obrigatório'

const loadingAtom = atom<boolean>(false)

const FormSchema = z.object({
  name: z.string({ required_error }).min(2, {
    message: 'Seu nome deve ter pelo menos 2 caracteres',
  }),
  email: z.string({ required_error }).email({
    message: 'Informe um e-mail válido',
  }),
  phone: z.string({ required_error }).min(14, {
    message: 'Informe um telefone válido',
  }),
})

export default function LeadFormDownload({
  title,
  callbackUrl,
  name,
  setOpen,
}: {
  title: string
  callbackUrl?: string
  name?: string
  setOpen: (open: boolean) => void
}) {
  const $loading = useStore(loadingAtom)

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  })

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    try {
      loadingAtom.set(true)

      await fetch('/api/proxy/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          {
            ...data,
            pageName: name,
            pageUri: location.href,
            type: 'download',
          },
          null,
          2,
        ),
      })

      if (callbackUrl) open(callbackUrl)

      form.reset()
      setOpen(false)

      loadingAtom.set(false)
    } catch (error) {
      logger(
        'LEAD FORM DOWNLOAD',
        JSON.stringify({
          name: error?.name,
          message: error?.message,
        }),
      )
      loadingAtom.set(false)
      toast({
        variant: 'destructive',
        title: 'Não foi possível liberar o conteúdo.',
        description: 'Houve um problema com a sua requisição.',
      })
    }
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="p-6 space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Nome completo"
                      {...field}
                      invalid={!!fieldState.error}
                      left={<User className="h-4 w-4" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="E-mail"
                      autoComplete="username"
                      {...field}
                      invalid={!!fieldState.error}
                      left={<AtSign className="h-4 w-4" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="(DDD) + Whatsapp"
                      {...field}
                      invalid={!!fieldState.error}
                      left={<Phone className="h-4 w-4" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-full p-6 pt-0 space-y-3">
            <Button type="submit" className="w-full" disabled={$loading}>
              {title || 'Cadastrar'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
